import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft,
  BadgeCheck,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  ShieldCheck,
  Stethoscope,
} from "lucide-react";

export default function DirectorioDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/directorio/${id}`);
      setDetail(data);
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      if (e?.response?.status !== 404) toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Shell>
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="animate-spin" size={14} /> Cargando ficha…
        </div>
      </Shell>
    );
  }

  if (error && !detail) {
    return (
      <Shell>
        <div className="rounded-md border border-destructive/30 bg-destructive/5 text-destructive p-4">
          {error}
        </div>
        <Button onClick={() => navigate("/directorio")} className="mt-4" variant="outline">
          <ArrowLeft size={14} /> Volver al directorio
        </Button>
      </Shell>
    );
  }

  const especialidadLabel =
    detail.especialidad === "Otra" && detail.especialidad_otra
      ? detail.especialidad_otra
      : detail.especialidad;
  const d = detail.direccion || {};
  const hasDireccion =
    !!(d.calle || d.colonia || d.ciudad || d.estado || d.cp);
  const hasTelefono = !!(detail.telefono || "").trim();
  const hasCorreo = !!(detail.correo_contacto || "").trim();
  const hasContacto = hasTelefono || hasCorreo;
  const cedulaFmt = (detail.cedula_profesional || "").trim();
  const cedulaEspFmt = (detail.cedula_especialidad || "").trim();

  return (
    <Shell>
      <div className="mb-5">
        <Link to="/directorio" data-testid="directorio-detail-back">
          <Button variant="ghost" size="sm">
            <ArrowLeft size={14} /> Regresar al directorio
          </Button>
        </Link>
      </div>

      <section
        className="rounded-xl border border-border bg-white p-6 sm:p-8"
        data-testid="directorio-detail-card"
      >
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 shrink-0 rounded-xl bg-[#0F2942] text-white grid place-items-center font-display font-semibold text-xl">
            {detail.iniciales || "MD"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB]">
              <Stethoscope size={12} className="inline mr-1 -mt-0.5" />
              {especialidadLabel || "Especialista"}
            </div>
            <h1
              className="mt-1 font-display text-3xl font-semibold text-[#0F2942] tracking-tight"
              data-testid="directorio-detail-nombre"
            >
              {detail.nombre_completo || "Especialista"}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {cedulaFmt && (
                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-0.5 text-[11px] font-medium text-foreground">
                  <BadgeCheck size={12} className="text-[#2563EB]" />
                  Cédula prof. <span className="font-mono">{cedulaFmt}</span>
                </span>
              )}
              {cedulaEspFmt && (
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-0.5 text-[11px] font-medium text-foreground"
                  data-testid="directorio-detail-ced-esp"
                >
                  <BadgeCheck size={12} className="text-[#2563EB]" />
                  Cédula esp. <span className="font-mono">{cedulaEspFmt}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-6">
          {hasContacto ? (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
                Contacto directo
              </div>
              <ul className="space-y-2 text-sm text-foreground">
                {hasTelefono && (
                  <li className="flex items-center gap-2">
                    <Phone size={14} className="text-[#2563EB]" />
                    <a
                      href={`tel:+52${detail.telefono}`}
                      className="hover:underline tabular-nums"
                      data-testid="directorio-detail-tel"
                    >
                      +52 {formatPhone(detail.telefono)}
                    </a>
                  </li>
                )}
                {hasCorreo && (
                  <li className="flex items-center gap-2">
                    <Mail size={14} className="text-[#2563EB]" />
                    <a
                      href={`mailto:${detail.correo_contacto}`}
                      className="hover:underline break-all"
                      data-testid="directorio-detail-mail"
                    >
                      {detail.correo_contacto}
                    </a>
                  </li>
                )}
              </ul>
              {hasTelefono && <WhatsAppButton medico={detail} className="mt-3 w-full" />}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground rounded-md border border-dashed border-border bg-muted/30 p-3">
              Este especialista aún no ha capturado datos de contacto directo.
            </div>
          )}

          {hasDireccion && (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
                Consultorio
              </div>
              <div
                className="text-sm text-foreground flex gap-2"
                data-testid="directorio-detail-direccion"
              >
                <MapPin size={14} className="text-[#2563EB] shrink-0 mt-0.5" />
                <div>
                  {d.calle && <div>{d.calle}</div>}
                  {(d.colonia || d.cp) && (
                    <div className="text-muted-foreground">
                      {d.colonia && `Col. ${d.colonia}`}
                      {d.colonia && d.cp && " · "}
                      {d.cp && `CP ${d.cp}`}
                    </div>
                  )}
                  {(d.ciudad || d.estado) && (
                    <div className="text-muted-foreground">
                      {[d.ciudad, d.estado].filter(Boolean).join(", ")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {detail.cofepris_aviso ? (
          <div className="mt-6 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground flex items-start gap-2">
            <ShieldCheck size={14} className="text-[#2563EB] shrink-0 mt-0.5" />
            <div>
              Aviso de funcionamiento COFEPRIS:{" "}
              <span className="font-mono text-foreground">{detail.cofepris_aviso}</span>
            </div>
          </div>
        ) : null}

        <div className="mt-5 text-[11px] text-muted-foreground leading-relaxed">
          Los datos mostrados provienen del perfil profesional declarado por el médico y de su
          suscripción vigente en Siempre Salud. Siempre Salud no realiza citas ni intermedia consultas
          — el contacto es directo entre persona usuaria y médico.
        </div>
      </section>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10">{children}</main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}

function formatPhone(t) {
  if (!t || t.length !== 10) return t || "";
  return `${t.slice(0, 2)} ${t.slice(2, 6)} ${t.slice(6)}`;
}

// Shared WhatsApp CTA — deep-link + best-effort tracking via public endpoint.
export function WhatsAppButton({ medico, className = "", compact = false }) {
  const tel = (medico?.telefono || "").replace(/\D/g, "");
  if (tel.length !== 10) return null;

  const message = encodeURIComponent(
    `Hola, quisiera agendar una consulta contigo desde Siempre Salud`
  );
  const waHref = `https://wa.me/52${tel}?text=${message}`;

  const onClick = () => {
    // Fire-and-forget tracking. We don't await/gate the redirect on this call.
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      fetch(`${base}/api/directorio/${medico.id}/whatsapp-click`, {
        method: "POST",
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* no-op */
    }
  };

  return (
    <a
      href={waHref}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] hover:bg-[#1ebe5b] text-white font-medium ${
        compact ? "px-3 py-1.5 text-xs" : "px-4 py-2.5 text-sm"
      } transition-colors ${className}`}
      data-testid={compact ? "wa-cta-card" : "wa-cta-detail"}
    >
      <MessageCircle size={compact ? 12 : 14} />
      {compact ? "WhatsApp" : "Contactar por WhatsApp"}
    </a>
  );
}
